// Shared design tokens for the recruiter workspace (and the shared components it renders).
// The palette is the existing one — this file only fixes fonts, type scale, radii and spacing
// so every page lines up. Import the tokens in styled/sx code instead of hard-coding values.
import { createTheme } from '@mui/material/styles';

// ─── Fonts (always with a fallback stack, never a bare family name) ───────────
export const FONT_BODY = "'Plus Jakarta Sans', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
export const FONT_DISPLAY = "'Syne', 'Plus Jakarta Sans', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
export const FONT_MONO = "'JetBrains Mono', SFMono-Regular, Menlo, Consolas, monospace";

// ─── Palette (unchanged — names for the colours already used everywhere) ──────
export const COLORS = {
  primary: '#6750A4',
  primaryDark: '#4F378B',
  primaryDeep: '#2D1B5E',
  primaryLight: '#7C5CBF',
  ink: '#3D2B6B',        // body text
  muted: '#8B7DB5',      // secondary text
  faint: '#B0A0D0',      // placeholder / empty-state text
  border: '#EDE7F6',
  borderStrong: '#C4B0E8',
  surface: '#FFFFFF',
  surfaceTint: '#FAF7FF',
  background: '#F3EEF9',
  accentSoft: '#E8DEF8',
  success: '#2E7D32',
  successSoft: '#E8F5E9',
  warning: '#E65100',
  warningSoft: '#FFF3E0',
  error: '#B3261E',
  errorSoft: '#FDECEA',
  info: '#1565C0',
  infoSoft: '#E3F2FD',
};

// ─── Type scale (px). Use only these sizes. ──────────────────────────────────
// overline: uppercase labels, table headers, pills · caption: meta text, helper text
// small: table cells, dense lists · body: paragraphs, inputs · cardTitle · section · page · stat
export const TYPE = {
  overline: 11,
  caption: 12,
  small: 13,
  body: 14,
  cardTitle: 16,
  section: 20,
  page: 28,
  stat: 32,
};

// ─── Radii, spacing, shadows ─────────────────────────────────────────────────
export const RADIUS = { sm: 8, md: 12, lg: 16, pill: 999 };
export const SPACE = (n) => n * 8; // 8px grid: SPACE(1)=8, SPACE(2)=16, SPACE(3)=24 …
export const SHADOW = {
  card: '0 1px 3px rgba(79,55,139,0.06), 0 4px 16px rgba(79,55,139,0.06)',
  cardHover: '0 8px 24px rgba(79,55,139,0.14)',
  panel: '0 4px 24px rgba(79,55,139,0.08)',
};

// Every tab/page: content sits on the lavender canvas with this padding; sections are white cards.
export const PAGE_PADDING = 32;
// Control heights: inputs/selects use md, buttons md (default) or sm (in tables).
export const CONTROL_HEIGHT = { lg: 40, md: 36, sm: 32 };

// Single-line truncation for titles/names inside cards and table cells.
export const ELLIPSIS = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
export const ELLIPSIS_CSS = 'overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;';

// "Round2_Suggested" → "Round 2 Suggested", "OnHold_TestPassed" → "On Hold Test Passed".
export const humanizeStatus = (s) => String(s ?? '')
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z0-9])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim();

// ─── MUI theme: makes every un-styled MUI text (Tabs, Buttons, inputs, menus,
// dialogs, tooltips) use the same fonts and sizes instead of Roboto defaults.
const recruiterTheme = createTheme({
  palette: {
    primary: { main: COLORS.primary, dark: COLORS.primaryDark, light: COLORS.primaryLight },
    text: { primary: COLORS.ink, secondary: COLORS.muted },
    error: { main: COLORS.error },
    success: { main: COLORS.success },
    warning: { main: COLORS.warning },
    info: { main: COLORS.info },
    background: { default: COLORS.background, paper: COLORS.surface },
    divider: COLORS.border,
  },
  shape: { borderRadius: RADIUS.sm },
  typography: {
    fontFamily: FONT_BODY,
    fontSize: TYPE.body,
    h1: { fontFamily: FONT_DISPLAY, fontWeight: 800 },
    h2: { fontFamily: FONT_DISPLAY, fontWeight: 800 },
    h3: { fontFamily: FONT_DISPLAY, fontWeight: 800 },
    h4: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.page },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section },
    h6: { fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.cardTitle },
    subtitle1: { fontSize: TYPE.body, fontWeight: 600 },
    subtitle2: { fontSize: TYPE.small, fontWeight: 600 },
    body1: { fontSize: TYPE.body },
    body2: { fontSize: TYPE.small },
    caption: { fontSize: TYPE.caption },
    overline: { fontSize: TYPE.overline, fontWeight: 700, letterSpacing: '0.08em' },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: { styleOverrides: { body: { fontFamily: FONT_BODY } } },
    MuiTab: { styleOverrides: { root: { fontFamily: FONT_BODY, fontWeight: 600, fontSize: TYPE.small, textTransform: 'none', minHeight: 44 } } },
    MuiTableCell: { styleOverrides: { root: { fontFamily: FONT_BODY, fontSize: TYPE.small, verticalAlign: 'middle' } } },
    MuiInputLabel: { styleOverrides: { root: { fontFamily: FONT_BODY, fontSize: TYPE.body } } },
    MuiInputBase: { styleOverrides: { root: { fontFamily: FONT_BODY, fontSize: TYPE.body } } },
    MuiMenuItem: { styleOverrides: { root: { fontFamily: FONT_BODY, fontSize: TYPE.body } } },
    MuiChip: { styleOverrides: { root: { fontFamily: FONT_BODY, fontWeight: 600 }, label: { fontSize: TYPE.caption } } },
    MuiTooltip: { styleOverrides: { tooltip: { fontFamily: FONT_BODY, fontSize: TYPE.caption } } },
    MuiDialogTitle: { styleOverrides: { root: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: TYPE.section } } },
    MuiAlert: { styleOverrides: { root: { fontFamily: FONT_BODY, fontSize: TYPE.small, alignItems: 'center' } } },
  },
});

export default recruiterTheme;
